import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin, TrendingUp, TrendingDown, BarChart3, Shield, Home,
  RefreshCw, Zap, Star, AlertTriangle, CheckCircle, Building2,
  DollarSign, FileText, Key, Calendar, BarChart2, Clock, Target,
  Droplets, GraduationCap, Landmark, CreditCard
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtMoney(n) {
  if (!n && n !== 0) return "N/A";
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
}

function GradeChip({ grade, label }) {
  const cfg = {
    "A+": "bg-[#00FF88]/10 border-[#00FF88]/30 text-[#00FF88]",
    "A":  "bg-[#00FF88]/10 border-[#00FF88]/30 text-[#00FF88]",
    "B+": "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]",
    "B":  "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]",
    "C+": "bg-[#FF9F0A]/10 border-[#FF9F0A]/30 text-[#FF9F0A]",
    "C":  "bg-[#FF9F0A]/10 border-[#FF9F0A]/30 text-[#FF9F0A]",
    "D":  "bg-red-500/10 border-red-500/30 text-[#FF3B30]",
    "growing":  "bg-[#00FF88]/10 border-[#00FF88]/30 text-[#00FF88]",
    "stable":   "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]",
    "declining":"bg-red-500/10 border-red-500/30 text-[#FF3B30]",
    "low":      "bg-[#00FF88]/10 border-[#00FF88]/30 text-[#00FF88]",
    "moderate": "bg-[#FF9F0A]/10 border-[#FF9F0A]/30 text-[#FF9F0A]",
    "high":     "bg-red-500/10 border-red-500/30 text-[#FF3B30]",
  };
  return (
    <div className="text-center">
      <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">{label}</p>
      <span className={`px-3 py-1 border rounded-full font-bold text-sm ${cfg[grade] || "bg-white/[0.04] border-white/[0.08] text-white"}`}>
        {grade}
      </span>
    </div>
  );
}

export function CompsPanel({ deal, onArvUpdate }) {
  const [comps, setComps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [selectedComps, setSelectedComps] = useState([]);
  const [applyingArv, setApplyingArv] = useState(false);
  const [arvMethod, setArvMethod] = useState("avg_ppsf");
  const [dateAdjPct, setDateAdjPct] = useState(0); // % adjustment per month for older sales
  const [distAdjPct, setDistAdjPct] = useState(0); // % adjustment per mile from subject

  const fetchComps = async () => {
    setLoading(true);
    setNotFound(false);
    setSelectedComps([]);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/attom/comps`,
        { address: deal.property_address, radius: 0.5 },
        { withCredentials: true }
      );
      setComps(res.data.comps || []);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else toast.error(err.response?.data?.detail || "Comps lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleComp = (comp, index) => {
    const compWithIndex = { ...comp, _index: index };
    const isSelected = selectedComps.some(c => c._index === index);
    if (isSelected) {
      setSelectedComps(selectedComps.filter(c => c._index !== index));
    } else {
      setSelectedComps([...selectedComps, compWithIndex]);
    }
  };

  // Calculate adjusted price for a comp based on date and distance
  const getAdjustedPrice = (comp) => {
    if (!comp.sale_price) return null;
    let adj = comp.sale_price;
    // Date adjustment
    if (dateAdjPct !== 0 && comp.sale_date) {
      const months = Math.round((Date.now() - new Date(comp.sale_date).getTime()) / (30.44 * 24 * 60 * 60 * 1000));
      adj = adj * (1 + (dateAdjPct / 100) * months);
    }
    // Distance adjustment
    if (distAdjPct !== 0 && comp.distance_mi) {
      adj = adj * (1 - (distAdjPct / 100) * comp.distance_mi);
    }
    return Math.round(adj);
  };

  const applySelectedCompsToArv = async () => {
    if (selectedComps.length === 0) {
      toast.error("Select at least one comp");
      return;
    }
    setApplyingArv(true);
    try {
      // Use adjusted prices if adjustments are set
      const adjustedComps = selectedComps.map(c => ({
        address: c.address,
        sale_price: getAdjustedPrice(c) || c.sale_price,
        sqft: c.sqft,
        price_per_sqft: c.sqft && (getAdjustedPrice(c) || c.sale_price) ? Math.round((getAdjustedPrice(c) || c.sale_price) / c.sqft) : c.price_per_sqft,
        sale_date: c.sale_date,
        beds: c.beds,
        baths: c.baths,
        distance_mi: c.distance_mi,
      }));
      const res = await axios.post(
        `${BACKEND_URL}/api/attom/apply-selected-comps`,
        {
          deal_id: deal.deal_id,
          selected_comps: adjustedComps,
          adjustment_method: arvMethod,
          subject_sqft: deal.sqft || deal.attom_cache?.property?.living_sqft,
        },
        { withCredentials: true }
      );
      toast.success(`ARV set to ${fmtMoney(res.data.calculated_arv)}`);
      if (onArvUpdate) onArvUpdate(res.data.calculated_arv);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply ARV");
    } finally {
      setApplyingArv(false);
    }
  };

  // Calculate preview ARV from selected comps with adjustments
  const previewArv = () => {
    if (selectedComps.length === 0) return null;
    const valid = selectedComps.filter(c => c.sale_price);
    if (valid.length === 0) return null;

    const adjustedPrices = valid.map(c => getAdjustedPrice(c) || c.sale_price);

    if (arvMethod === "avg_price") {
      return Math.round(adjustedPrices.reduce((s, p) => s + p, 0) / adjustedPrices.length);
    } else if (arvMethod === "avg_ppsf") {
      const withSqft = valid.filter(c => c.sqft);
      if (withSqft.length === 0) return Math.round(adjustedPrices.reduce((s, p) => s + p, 0) / adjustedPrices.length);
      const avgPpsf = withSqft.reduce((sum, c) => sum + ((getAdjustedPrice(c) || c.sale_price) / c.sqft), 0) / withSqft.length;
      const subjectSqft = deal.sqft || deal.attom_cache?.property?.living_sqft;
      if (!subjectSqft) return Math.round(adjustedPrices.reduce((s, p) => s + p, 0) / adjustedPrices.length);
      return Math.round(avgPpsf * subjectSqft);
    }
    return Math.round(adjustedPrices.reduce((s, p) => s + p, 0) / adjustedPrices.length);
  };

  const getMonthsAgo = (dateStr) => {
    if (!dateStr) return null;
    return Math.round((Date.now() - new Date(dateStr).getTime()) / (30.44 * 24 * 60 * 60 * 1000));
  };

  const avgPpsf = comps?.length
    ? Math.round(comps.filter(c => c.price_per_sqft).reduce((a, c) => a + c.price_per_sqft, 0) / comps.filter(c => c.price_per_sqft).length)
    : null;
  const avgPrice = comps?.length
    ? Math.round(comps.filter(c => c.sale_price).reduce((a, c) => a + c.sale_price, 0) / comps.filter(c => c.sale_price).length)
    : null;

  const hasAdjustments = dateAdjPct !== 0 || distAdjPct !== 0;

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-[#0A84FF]/10 border border-[#0A84FF]/20 rounded flex items-center justify-center">
            <BarChart2 className="w-3.5 h-3.5 text-[#0A84FF]" />
          </div>
          <div>
            <p className="text-white text-sm font-semibold">Comparable Sales</p>
            <p className="text-white/30 text-[10px]">Recent sold comps within 0.5 mi — ATTOM verified</p>
          </div>
        </div>
        {!comps && (
          <button onClick={fetchComps} disabled={loading}
            data-testid="fetch-comps-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A84FF] hover:bg-[#0A84FF]/90 text-white text-xs font-bold rounded-lg transition-all disabled:opacity-50">
            {loading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <BarChart2 className="w-3 h-3" />}
            Pull Comps
          </button>
        )}
        {comps && (
          <button onClick={fetchComps} disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1.5 border border-white/[0.08] text-white/40 hover:text-white rounded-lg text-xs transition-all">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        )}
      </div>

      {notFound && (
        <p className="text-white/30 text-xs text-center py-3">No recent comps found within 0.5 mi. Try expanding the radius.</p>
      )}

      {loading && (
        <div className="flex justify-center py-4"><RefreshCw className="w-5 h-5 text-[#0A84FF] animate-spin" /></div>
      )}

      {comps && comps.length > 0 && (
        <>
          {/* Avg summary row */}
          <div className="grid grid-cols-2 gap-3">
            {avgPrice && (
              <div className="bg-[#0A84FF]/[0.04] border border-[#0A84FF]/15 rounded-lg p-3 text-center">
                <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Avg Sale Price</p>
                <p className="text-[#0A84FF] font-black text-xl font-mono">{fmtMoney(avgPrice)}</p>
                <p className="text-white/20 text-[10px] mt-0.5">{comps.length} comps</p>
              </div>
            )}
            {avgPpsf && (
              <div className="bg-[#D4AF37]/[0.04] border border-[#D4AF37]/15 rounded-lg p-3 text-center">
                <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Avg $/sqft</p>
                <p className="text-[#D4AF37] font-black text-xl font-mono">${avgPpsf}</p>
                <p className="text-white/20 text-[10px] mt-0.5">price per sqft</p>
              </div>
            )}
          </div>
          
          {/* Selection Instructions */}
          <div className="bg-[#00FF88]/[0.04] border border-[#00FF88]/15 rounded-lg p-3">
            <p className="text-[#00FF88] text-xs font-semibold mb-1">Select Best Comps for ARV</p>
            <p className="text-white/40 text-[10px]">Click on comps that best represent your subject property. Selected comps will be used to calculate ARV.</p>
          </div>
          
          {/* Comp rows with selection */}
          <div className="space-y-2">
            {comps.map((c, i) => {
              const isSelected = selectedComps.some(sc => sc._index === i);
              const monthsAgo = getMonthsAgo(c.sale_date);
              const adjPrice = hasAdjustments ? getAdjustedPrice(c) : null;
              return (
                <div 
                  key={i} 
                  onClick={() => toggleComp(c, i)}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    isSelected 
                      ? "bg-[#00FF88]/10 border-2 border-[#00FF88]/50" 
                      : "bg-white/[0.02] border border-white/[0.04] hover:border-white/10"
                  }`}
                  data-testid={`comp-row-${i}`}
                >
                  <div className="flex items-center gap-3">
                    {/* Selection indicator */}
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                      isSelected ? "bg-[#00FF88] text-black" : "bg-white/[0.06] text-white/40"
                    }`}>
                      {isSelected ? <CheckCircle className="w-3 h-3" /> : <span className="text-[10px] font-mono">{i + 1}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium truncate ${isSelected ? "text-[#00FF88]" : "text-white"}`}>
                        {c.address || "Address N/A"}
                        {c.city_state && <span className="text-white/30 ml-1">{c.city_state}</span>}
                      </p>
                      {/* Property details row */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                        {c.beds && <span className="text-[11px] text-white/50 font-medium">{c.beds} bd</span>}
                        {c.baths && <span className="text-[11px] text-white/50 font-medium">{c.baths} ba</span>}
                        {c.sqft && <span className="text-[11px] text-white/50 font-medium">{Number(c.sqft).toLocaleString()} sqft</span>}
                        {c.year_built && <span className="text-[11px] text-white/30">Built {c.year_built}</span>}
                        {c.lot_sqft && <span className="text-[11px] text-white/30">Lot {Number(c.lot_sqft).toLocaleString()} sqft</span>}
                      </div>
                      {/* Distance + Date row */}
                      <div className="flex items-center gap-3 mt-0.5">
                        {c.distance_mi != null && (
                          <span className="text-[10px] text-blue-400/80 font-mono">
                            {c.distance_mi} mi
                          </span>
                        )}
                        {c.sale_date && (
                          <span className="text-[10px] text-white/30">
                            Sold {new Date(c.sale_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                            {monthsAgo != null && <span className="text-white/20 ml-1">({monthsAgo}mo ago)</span>}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-sm font-mono text-[#00FF88]">
                        {fmtMoney(c.sale_price)}
                      </p>
                      {c.price_per_sqft && (
                        <p className="text-white/30 text-[10px] font-mono">${c.price_per_sqft}/sqft</p>
                      )}
                      {adjPrice && adjPrice !== c.sale_price && (
                        <p className="text-[#D4AF37] text-[10px] font-mono mt-0.5">Adj: {fmtMoney(adjPrice)}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Date & Distance Adjustments */}
          {comps.length > 0 && (
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 space-y-2">
              <p className="text-[10px] text-white/40 uppercase font-bold tracking-wider">Adjustments</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-white/40 block mb-1">Date Adj (%/month)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.1"
                      value={dateAdjPct}
                      onChange={(e) => setDateAdjPct(parseFloat(e.target.value) || 0)}
                      className="w-full h-8 bg-black border border-white/[0.08] rounded px-2 text-white text-xs font-mono"
                      data-testid="date-adj-input"
                      placeholder="e.g. 0.5"
                    />
                    <span className="text-[10px] text-white/30 flex-shrink-0">%/mo</span>
                  </div>
                  <p className="text-[9px] text-white/20 mt-0.5">+ value = appreciating market</p>
                </div>
                <div>
                  <label className="text-[10px] text-white/40 block mb-1">Distance Adj (%/mile)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.5"
                      value={distAdjPct}
                      onChange={(e) => setDistAdjPct(parseFloat(e.target.value) || 0)}
                      className="w-full h-8 bg-black border border-white/[0.08] rounded px-2 text-white text-xs font-mono"
                      data-testid="dist-adj-input"
                      placeholder="e.g. 2"
                    />
                    <span className="text-[10px] text-white/30 flex-shrink-0">%/mi</span>
                  </div>
                  <p className="text-[9px] text-white/20 mt-0.5">Discount for further comps</p>
                </div>
              </div>
            </div>
          )}
          
          {/* ARV Calculation Panel */}
          {selectedComps.length > 0 && (
            <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#00FF88]/10 border border-[#D4AF37]/30 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-semibold">Calculate ARV from Selection</p>
                  <p className="text-white/40 text-[10px]">{selectedComps.length} comp{selectedComps.length > 1 ? "s" : ""} selected</p>
                </div>
                {previewArv() && (
                  <div className="text-right">
                    <p className="text-[10px] text-white/40 uppercase">Preview ARV</p>
                    <p className="text-[#D4AF37] font-black text-2xl font-mono">{fmtMoney(previewArv())}</p>
                  </div>
                )}
              </div>
              
              {/* Method selector */}
              <div className="flex items-center gap-2">
                <span className="text-white/40 text-xs">Method:</span>
                <select 
                  value={arvMethod} 
                  onChange={(e) => setArvMethod(e.target.value)}
                  className="bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-xs"
                  data-testid="arv-method-select"
                >
                  <option value="avg_ppsf">Avg $/sqft × Subject Sqft</option>
                  <option value="avg_price">Average Sale Price</option>
                  <option value="weighted">Weighted (recency + size)</option>
                </select>
              </div>
              
              <button
                onClick={applySelectedCompsToArv}
                disabled={applyingArv}
                data-testid="apply-arv-btn"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#D4AF37] hover:bg-[#c4a030] text-black font-bold rounded-lg transition-all disabled:opacity-50"
              >
                {applyingArv ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Target className="w-4 h-4" />
                )}
                Apply to Deal ARV
              </button>
            </div>
          )}
        </>
      )}

      {!comps && !notFound && !loading && (
        <p className="text-white/20 text-xs text-center py-2">Pull real comparable sales from ATTOM to validate your ARV</p>
      )}
    </div>
  );
}


function SalesHistoryPanel({ deal }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/attom/sales-history`,
        { address: deal.property_address },
        { withCredentials: true }
      );
      setHistory(res.data);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else toast.error(err.response?.data?.detail || "History lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const entries = history?.history || [];
  const maxPrice = entries.length ? Math.max(...entries.filter(h => h.sale_price).map(h => h.sale_price)) : 0;

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-[#AF52DE]/10 border border-[#AF52DE]/20 rounded flex items-center justify-center">
            <Clock className="w-3.5 h-3.5 text-[#AF52DE]" />
          </div>
          <div>
            <p className="text-white text-sm font-semibold">Sales History</p>
            <p className="text-white/30 text-[10px]">Every prior sale of this property from ATTOM</p>
          </div>
        </div>
        {!history && (
          <button onClick={fetchHistory} disabled={loading}
            data-testid="fetch-history-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#AF52DE] hover:bg-[#AF52DE]/90 text-white text-xs font-bold rounded-lg transition-all disabled:opacity-50">
            {loading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />}
            Load History
          </button>
        )}
        {history && (
          <button onClick={fetchHistory} disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1.5 border border-white/[0.08] text-white/40 hover:text-white rounded-lg text-xs transition-all">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        )}
      </div>

      {notFound && (
        <p className="text-white/30 text-xs text-center py-3">No sales history found for this property address.</p>
      )}
      {loading && (
        <div className="flex justify-center py-4"><RefreshCw className="w-5 h-5 text-[#AF52DE] animate-spin" /></div>
      )}

      {history && entries.length > 0 && (
        <div className="space-y-3">
          {/* Property specs from history */}
          {(history.beds || history.sqft || history.year_built) && (
            <div className="flex items-center gap-3 text-xs text-white/40 pb-3 border-b border-white/[0.05]">
              {history.beds && <span>{history.beds} bed</span>}
              {history.baths && <span>{history.baths} bath</span>}
              {history.sqft && <span>{Number(history.sqft).toLocaleString()} sqft</span>}
              {history.year_built && <span>Built {history.year_built}</span>}
              {history.prop_type && <span className="capitalize">{history.prop_type.toLowerCase()}</span>}
            </div>
          )}
          {/* Timeline */}
          <div className="relative pl-4">
            <div className="absolute left-1.5 top-2 bottom-2 w-px bg-white/[0.08]" />
            {entries.map((h, i) => {
              const barPct = maxPrice > 0 && h.sale_price ? Math.round((h.sale_price / maxPrice) * 100) : 0;
              const isLatest = i === 0;
              return (
                <div key={i} className="relative mb-4 last:mb-0">
                  <div className={`absolute -left-[11px] top-1 w-3 h-3 rounded-full border-2 ${
                    isLatest ? "bg-[#AF52DE] border-[#AF52DE]" : "bg-[#09090B] border-white/20"
                  }`} />
                  <div className="pl-4">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <span className="text-white text-sm font-bold font-mono">
                          {h.sale_price ? fmtMoney(h.sale_price) : "Price N/A"}
                        </span>
                        {h.sale_type && h.sale_type !== "Resale" && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-white/[0.04] border border-white/[0.06] rounded text-white/40">{h.sale_type}</span>
                        )}
                      </div>
                      <span className="text-white/30 text-xs">
                        {h.sale_date
                          ? new Date(h.sale_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                          : "Date N/A"}
                      </span>
                    </div>
                    {barPct > 0 && (
                      <div className="h-1 bg-white/[0.04] rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${isLatest ? "bg-[#AF52DE]" : "bg-white/20"}`}
                          style={{ width: `${barPct}%` }}
                        />
                      </div>
                    )}
                    {h.price_per_sqft && (
                      <p className="text-white/20 text-[10px] mt-1">${Math.round(h.price_per_sqft)}/sqft</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!history && !notFound && !loading && (
        <p className="text-white/20 text-xs text-center py-2">View every prior sale of this property to spot flip patterns and equity growth</p>
      )}
    </div>
  );
}


function AttomPanel({ attom, attomUnconfigured, loadingAttom, fetchAttom }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded flex items-center justify-center">
            <Building2 className="w-3.5 h-3.5 text-[#D4AF37]" />
          </div>
          <div>
            <p className="text-white text-sm font-semibold">Real Property Records</p>
            <p className="text-white/30 text-[10px]">Live data from ATTOM — AVM, tax assessment, property specs</p>
          </div>
        </div>
        {!attom && (
          <button
            onClick={fetchAttom}
            disabled={loadingAttom}
            data-testid="fetch-attom-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black text-xs font-bold rounded-lg transition-all disabled:opacity-50"
          >
            {loadingAttom ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Building2 className="w-3 h-3" />}
            Fetch Real Data
          </button>
        )}
        {attom && (
          <button onClick={fetchAttom} disabled={loadingAttom}
            className="flex items-center gap-1.5 px-2.5 py-1.5 border border-white/[0.08] text-white/40 hover:text-white rounded-lg text-xs transition-all">
            <RefreshCw className={`w-3 h-3 ${loadingAttom ? "animate-spin" : ""}`} />
          </button>
        )}
      </div>

      {attomUnconfigured && (
        <div className="flex items-start gap-3 p-3 bg-[#FF9F0A]/[0.06] border border-[#FF9F0A]/20 rounded-lg">
          <Key className="w-4 h-4 text-[#FF9F0A] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[#FF9F0A] text-xs font-semibold">ATTOM API Key Required</p>
            <p className="text-white/40 text-[11px] mt-0.5">
              Add your ATTOM_API_KEY to the backend environment. Get a free trial at{" "}
              <a href="https://www.attomdata.com" target="_blank" rel="noopener noreferrer" className="underline text-[#D4AF37]">attomdata.com</a>.
            </p>
          </div>
        </div>
      )}

      {attom && (
        <div className="grid grid-cols-2 gap-4">
          {attom.avm && (
            <div className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-4">
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3">AVM Estimate</p>
              <p className="text-[#D4AF37] font-black text-2xl font-mono">{fmtMoney(attom.avm.estimated_value)}</p>
              {attom.avm.low && attom.avm.high && (
                <p className="text-white/40 text-xs mt-1">{fmtMoney(attom.avm.low)} – {fmtMoney(attom.avm.high)}</p>
              )}
              {attom.avm.condition && (
                <span className="text-[10px] px-2 py-0.5 mt-2 inline-block bg-white/[0.04] border border-white/[0.06] rounded text-white/50">
                  Confidence: {attom.avm.condition}
                </span>
              )}
            </div>
          )}
          {attom.property && (
            <div className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-4">
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3">Property Specs</p>
              <div className="space-y-1.5">
                {attom.property.year_built && (
                  <div className="flex justify-between text-xs"><span className="text-white/40">Year Built</span><span className="text-white">{attom.property.year_built}</span></div>
                )}
                {attom.property.beds && (
                  <div className="flex justify-between text-xs"><span className="text-white/40">Beds / Baths</span><span className="text-white">{attom.property.beds} / {attom.property.baths}</span></div>
                )}
                {attom.property.living_sqft && (
                  <div className="flex justify-between text-xs"><span className="text-white/40">Living Area</span><span className="text-white">{Number(attom.property.living_sqft).toLocaleString()} sqft</span></div>
                )}
                {attom.property.lot_sqft && (
                  <div className="flex justify-between text-xs"><span className="text-white/40">Lot Size</span><span className="text-white">{Number(attom.property.lot_sqft).toLocaleString()} sqft</span></div>
                )}
                {attom.property.prop_type && (
                  <div className="flex justify-between text-xs"><span className="text-white/40">Type</span><span className="text-white capitalize">{attom.property.prop_type.toLowerCase()}</span></div>
                )}
              </div>
            </div>
          )}
          {attom.assessment && (
            <div className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-4">
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3">Tax Assessment</p>
              <div className="space-y-1.5">
                {attom.assessment.assessed_value && (
                  <div className="flex justify-between text-xs"><span className="text-white/40">Assessed Value</span><span className="text-white">{fmtMoney(attom.assessment.assessed_value)}</span></div>
                )}
                {attom.assessment.market_value && (
                  <div className="flex justify-between text-xs"><span className="text-white/40">Market Value</span><span className="text-[#00FF88]">{fmtMoney(attom.assessment.market_value)}</span></div>
                )}
                {attom.assessment.tax_year && (
                  <div className="flex justify-between text-xs"><span className="text-white/40">Tax Year</span><span className="text-white">{attom.assessment.tax_year}</span></div>
                )}
              </div>
            </div>
          )}
          {attom.tax && (
            <div className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-4">
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3">Property Tax</p>
              <p className="text-[#FF3B30] font-bold text-xl font-mono">{fmtMoney(attom.tax.tax_amount)}<span className="text-white/30 text-sm font-normal">/yr</span></p>
              {attom.tax.tax_year && <p className="text-white/30 text-xs mt-1">{attom.tax.tax_year} tax year</p>}
            </div>
          )}
        </div>
      )}

      {!attom && !attomUnconfigured && !loadingAttom && (
        <p className="text-white/20 text-xs text-center py-2">Click "Fetch Real Data" for live AVM, tax records, and specs from ATTOM</p>
      )}
      {loadingAttom && (
        <div className="flex justify-center py-4">
          <RefreshCw className="w-5 h-5 text-[#D4AF37] animate-spin" />
        </div>
      )}
    </div>
  );
}


function ExpandedAttomPanel({ data }) {
  if (!data) return null;
  
  return (
    <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2 pb-3 border-b border-white/[0.05]">
        <div className="w-6 h-6 bg-purple-500/10 border border-purple-500/20 rounded flex items-center justify-center">
          <CreditCard className="w-3.5 h-3.5 text-purple-400" />
        </div>
        <div>
          <p className="text-white text-sm font-semibold">Mortgage & Equity Analysis</p>
          <p className="text-white/30 text-[10px]">Loan history, estimated equity, and ownership details from ATTOM</p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {/* Estimated Equity */}
        {data.estimated_equity !== undefined && (
          <div className="bg-gradient-to-br from-[#00FF88]/5 to-transparent border border-[#00FF88]/20 rounded-lg p-4">
            <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-2">Estimated Equity</p>
            <p className={`font-black text-2xl font-mono ${data.estimated_equity >= 0 ? "text-[#00FF88]" : "text-red-400"}`}>
              {fmtMoney(data.estimated_equity)}
            </p>
            <p className="text-white/30 text-[10px] mt-1">AVM minus total loans</p>
          </div>
        )}
        
        {/* AVM with 12mo Change */}
        {data.avm && (
          <div className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-4">
            <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-2">AVM Estimate</p>
            <p className="text-[#D4AF37] font-black text-2xl font-mono">{fmtMoney(data.avm.estimated_value)}</p>
            {data.avm.value_change_12mo && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${data.avm.value_change_12mo >= 0 ? "text-[#00FF88]" : "text-red-400"}`}>
                {data.avm.value_change_12mo >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {data.avm.value_change_12mo >= 0 ? "+" : ""}{data.avm.value_change_12mo.toFixed(1)}% (12mo)
              </p>
            )}
          </div>
        )}
      </div>
      
      {/* Mortgage History */}
      {data.mortgages && data.mortgages.length > 0 && (
        <div className="space-y-3">
          <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold flex items-center gap-1.5">
            <Landmark className="w-3 h-3" /> Mortgage History
          </p>
          <div className="space-y-2">
            {data.mortgages.map((m, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-white/[0.02] border border-white/[0.04] rounded-lg">
                <div>
                  <p className="text-white text-sm font-semibold">{fmtMoney(m.amount)}</p>
                  <p className="text-white/40 text-[10px]">{m.lender || "Lender Unknown"}</p>
                </div>
                <div className="text-right">
                  {m.interest_rate && (
                    <p className="text-[#D4AF37] text-sm font-mono">{m.interest_rate}%</p>
                  )}
                  {m.term_years && (
                    <p className="text-white/30 text-[10px]">{m.term_years} yr term</p>
                  )}
                  {m.record_date && (
                    <p className="text-white/20 text-[10px]">
                      {new Date(m.record_date).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Expanded Assessment */}
      {data.assessment && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-3 text-center">
            <p className="text-[9px] text-white/30 uppercase">Assessed Total</p>
            <p className="text-white font-bold text-sm font-mono">{fmtMoney(data.assessment.assessed_total)}</p>
          </div>
          <div className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-3 text-center">
            <p className="text-[9px] text-white/30 uppercase">Land Value</p>
            <p className="text-white font-bold text-sm font-mono">{fmtMoney(data.assessment.assessed_land)}</p>
          </div>
          <div className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-3 text-center">
            <p className="text-[9px] text-white/30 uppercase">Improvements</p>
            <p className="text-white font-bold text-sm font-mono">{fmtMoney(data.assessment.assessed_improvements)}</p>
          </div>
        </div>
      )}
      
      {/* Area Info */}
      {data.area && (
        <div className="flex items-center gap-4 text-xs text-white/40 pt-2 border-t border-white/[0.05]">
          {data.area.neighborhood && <span>Neighborhood: {data.area.neighborhood}</span>}
          {data.area.county_name && <span>County: {data.area.county_name}</span>}
        </div>
      )}
    </div>
  );
}


export default function PropertyIntelligence({ deal }) {
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attom, setAttom] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [loadingAttom, setLoadingAttom] = useState(false);
  const [loadingExpanded, setLoadingExpanded] = useState(false);
  const [attomUnconfigured, setAttomUnconfigured] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/deals/${deal.deal_id}/property-intelligence`,
        {},
        { withCredentials: true }
      );
      setIntel(res.data);
      toast.success("Property intelligence generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAttom = async () => {
    setLoadingAttom(true);
    setAttomUnconfigured(false);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/attom/lookup`,
        { deal_id: deal.deal_id },
        { withCredentials: true }
      );
      setAttom(res.data);
      toast.success("Real property data loaded!");
    } catch (err) {
      if (err.response?.status === 503) {
        setAttomUnconfigured(true);
      } else {
        toast.error(err.response?.data?.detail || "ATTOM lookup failed.");
      }
    } finally {
      setLoadingAttom(false);
    }
  };

  const fetchExpandedAttom = async () => {
    setLoadingExpanded(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/attom/expanded-lookup`,
        { deal_id: deal.deal_id, address: deal.property_address },
        { withCredentials: true }
      );
      setExpanded(res.data);
      toast.success("Expanded property data loaded (mortgage, equity, assessment)!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Expanded lookup failed.");
    } finally {
      setLoadingExpanded(false);
    }
  };
  
  if (!intel) {
    return (
      <div className="space-y-5" data-testid="property-intelligence">
        {/* AI empty state */}
        <div className="flex flex-col items-center justify-center py-16 text-center space-y-6">
          <div className="w-16 h-16 rounded-2xl bg-[#0A84FF]/10 border border-[#0A84FF]/20 flex items-center justify-center">
            <MapPin className="w-8 h-8 text-[#0A84FF]" />
          </div>
          <div className="space-y-2 max-w-sm">
            <h3 className="text-white font-bold text-lg">Property Intelligence</h3>
            <p className="text-white/40 text-sm leading-relaxed">
              AI-powered market analysis for this property — location grade, ARV estimates, rental potential, neighborhood insights, and risk factors.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 max-w-sm w-full">
            {[
              { icon: MapPin, text: "Location grade & neighborhood" },
              { icon: TrendingUp, text: "ARV range & market trend" },
              { icon: BarChart3, text: "Rental potential & cap rate" },
            ].map((item, i) => (
              <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 text-center space-y-2">
                <item.icon className="w-5 h-5 text-[#0A84FF] mx-auto" />
                <p className="text-white/50 text-[11px]">{item.text}</p>
              </div>
            ))}
          </div>
          <button
            onClick={generate}
            disabled={loading}
            data-testid="generate-intel-btn"
            className="flex items-center gap-2 px-8 py-3 bg-[#0A84FF] hover:bg-[#0A84FF]/90 text-white font-bold rounded-lg transition-all disabled:opacity-50"
          >
            {loading ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Analyzing Property...</>
            ) : (
              <><MapPin className="w-4 h-4" /> Generate Property Intelligence</>
            )}
          </button>
          <p className="text-white/20 text-xs">Powered by Llama 3.3 70B · AI estimates only</p>
        </div>
        {/* ATTOM real data — available even before AI */}
        <AttomPanel attom={attom} attomUnconfigured={attomUnconfigured} loadingAttom={loadingAttom} fetchAttom={fetchAttom} />
        
        {/* Expanded ATTOM Data - Mortgage, Equity, etc */}
        {attom && !expanded && (
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4">
            <button
              onClick={fetchExpandedAttom}
              disabled={loadingExpanded}
              data-testid="fetch-expanded-btn"
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] rounded-lg text-white text-xs font-semibold transition-all disabled:opacity-50"
            >
              {loadingExpanded ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 text-[#D4AF37]" />
              )}
              Load Mortgage History & Equity Analysis
            </button>
          </div>
        )}
        
        {expanded && <ExpandedAttomPanel data={expanded} />}

        {/* Sales History — always available */}
        <SalesHistoryPanel deal={deal} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="intel-result">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-[#0A84FF]/10 border border-[#0A84FF]/20 rounded-lg flex items-center justify-center">
            <MapPin className="w-4 h-4 text-[#0A84FF]" />
          </div>
          <span className="text-white/60 text-sm font-medium">{intel.address}</span>
        </div>
        <button onClick={generate} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-white/[0.08] text-white/50 hover:text-white rounded-lg text-xs transition-all"
          data-testid="regenerate-intel-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Grades Row */}
      <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
        <div className="grid grid-cols-4 gap-4">
          <GradeChip grade={intel.location_grade} label="Location" />
          <GradeChip grade={intel.investment_grade} label="Investment" />
          <GradeChip grade={intel.market_trend} label="Market Trend" />
          <GradeChip grade={intel.neighborhood_profile?.crime_level} label="Crime Level" />
        </div>
      </div>

      {/* ARV Range */}
      {intel.arv_estimate && (
        <div className="bg-[#D4AF37]/[0.04] border border-[#D4AF37]/20 rounded-xl p-5">
          <p className="text-[10px] text-[#D4AF37]/70 uppercase tracking-wider font-semibold mb-3 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5 text-[#D4AF37]" /> AI ARV Estimate Range
          </p>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-center">
              <p className="text-[10px] text-white/30 mb-1">Conservative</p>
              <p className="text-white font-bold text-lg font-mono">{fmtMoney(intel.arv_estimate.low)}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-[#D4AF37]/70 mb-1">Mid Estimate</p>
              <p className="text-[#D4AF37] font-bold text-2xl font-mono">{fmtMoney(intel.arv_estimate.mid)}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-white/30 mb-1">Optimistic</p>
              <p className="text-white font-bold text-lg font-mono">{fmtMoney(intel.arv_estimate.high)}</p>
            </div>
          </div>
          {intel.arv_estimate.narrative && (
            <p className="text-white/40 text-xs">{intel.arv_estimate.narrative}</p>
          )}
        </div>
      )}

      {/* Neighborhood + Rental */}
      <div className="grid grid-cols-2 gap-4">
        {intel.neighborhood_profile && (
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
            <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3 flex items-center gap-1.5">
              <Home className="w-3.5 h-3.5" /> Neighborhood
            </p>
            <p className="text-white/60 text-sm leading-relaxed mb-3">{intel.neighborhood_profile.description}</p>
            <div className="space-y-1 text-xs">
              {intel.neighborhood_profile.school_rating && (
                <div className="flex justify-between">
                  <span className="text-white/30">Schools</span>
                  <span className="text-white/60">{intel.neighborhood_profile.school_rating}</span>
                </div>
              )}
              {intel.neighborhood_profile.walk_score && (
                <div className="flex justify-between">
                  <span className="text-white/30">Walkability</span>
                  <span className="text-white/60">
                    {typeof intel.neighborhood_profile.walk_score === 'number' 
                      ? `${intel.neighborhood_profile.walk_score}/100`
                      : typeof intel.neighborhood_profile.walk_score === 'string' && intel.neighborhood_profile.walk_score.length > 20
                        ? intel.neighborhood_profile.walk_score.split(' ').slice(0, 2).join(' ')
                        : intel.neighborhood_profile.walk_score}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        {intel.rental_potential && (
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
            <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> Rental Potential
            </p>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-white/40 text-xs">Est. Monthly Rent</span>
                <span className="text-[#00FF88] font-bold font-mono">{fmtMoney(intel.rental_potential.estimated_monthly_rent)}/mo</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/40 text-xs">Gross Rent Multiplier</span>
                <span className="text-white/70 font-mono text-sm">{intel.rental_potential.gross_rent_multiplier}x</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/40 text-xs">Est. Cap Rate</span>
                <span className="text-white/70 font-mono text-sm">{intel.rental_potential.cap_rate_estimate_pct}%</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Market Insights */}
      {intel.market_insights?.length > 0 && (
        <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
          <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3 flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" /> Market Insights
          </p>
          <ul className="space-y-2">
            {intel.market_insights.map((insight, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-white/60">
                <Zap className="w-3.5 h-3.5 text-[#0A84FF] mt-0.5 flex-shrink-0" />
                {insight}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Advantages + Risks */}
      <div className="grid grid-cols-2 gap-4">
        {intel.location_advantages?.length > 0 && (
          <div className="bg-[#00FF88]/[0.04] border border-[#00FF88]/10 rounded-xl p-5">
            <p className="text-[10px] text-[#00FF88]/60 uppercase tracking-wider font-semibold mb-3">Location Advantages</p>
            <ul className="space-y-1.5">
              {intel.location_advantages.map((a, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white/60">
                  <CheckCircle className="w-3.5 h-3.5 text-[#00FF88] mt-0.5 flex-shrink-0" /> {a}
                </li>
              ))}
            </ul>
          </div>
        )}
        {intel.location_risks?.length > 0 && (
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
            <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3">Location Risks</p>
            <ul className="space-y-1.5">
              {intel.location_risks.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white/60">
                  <AlertTriangle className="w-3.5 h-3.5 text-[#FF9F0A] mt-0.5 flex-shrink-0" /> {r}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Exit Strategy Fit */}
      {intel.exit_strategy_fit && (
        <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4">
          <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-2">Exit Strategy Assessment</p>
          <p className="text-white/60 text-sm">{intel.exit_strategy_fit}</p>
        </div>
      )}

      {/* ── ATTOM Real Property Data ─────────────────────────────── */}
      <AttomPanel attom={attom} attomUnconfigured={attomUnconfigured} loadingAttom={loadingAttom} fetchAttom={fetchAttom} />
      
      {/* Expanded ATTOM Data - Mortgage, Equity, etc */}
      {attom && !expanded && (
        <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4">
          <button
            onClick={fetchExpandedAttom}
            disabled={loadingExpanded}
            data-testid="fetch-expanded-btn-2"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] rounded-lg text-white text-xs font-semibold transition-all disabled:opacity-50"
          >
            {loadingExpanded ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4 text-[#D4AF37]" />
            )}
            Load Mortgage History & Equity Analysis
          </button>
        </div>
      )}
      
      {expanded && <ExpandedAttomPanel data={expanded} />}

      {/* ── Sales History ────────────────────────────────────────── */}
      <SalesHistoryPanel deal={deal} />

      <p className="text-white/20 text-[10px] border-t border-white/[0.04] pt-3">
        {intel.data_disclaimer || "AI-estimated. Always verify with licensed appraiser and public county records."}
      </p>
    </div>
  );
}
